import type { ColType } from '../../../../src/lib/excelExport';
import type { Planilha } from '../../../../src/lib/planilhaExcel';
import { dataParaSerialExcel } from '../../../../src/lib/planilhaExcel.ts';
import type { DevedorTop, FaixaAging, FaturaPainel, Kpis, PontoMensal } from '../types.ts';
import { valorDevido } from '../../../../src/lib/statusBoleto.ts';
import { DEFINICOES_KPI, dataVencimentoDe, diasPagamentoAposVencimento, somaValorDevido } from '../../../../src/lib/financeiro/definicoesKpi.ts';

// Monta as abas do Excel do Painel Financeiro Executivo como dados puros
// (cabeçalho, linhas, tipos). Quem gera o arquivo é `baixarPlanilhas`, que
// carrega a biblioteca de Excel só no clique. Separado para rodar sob node --test.

// O tipo Planilha e o conversor de data moram em src/lib/planilhaExcel.ts —
// a Central de Inadimplência monta as abas dela com as mesmas peças.
export type { Planilha } from '../../../../src/lib/planilhaExcel';
export { dataParaSerialExcel } from '../../../../src/lib/planilhaExcel.ts';

/** Descrição legível dos filtros globais, na ordem da barra de filtros. */
export interface FiltrosDescritos {
  periodo: string;
  unidadeGeradora: string;
  status: string;
  cliente: string;
}

const COLUNAS_BOLETOS: { header: string; tipo: ColType; largura: number; valor: (f: FaturaPainel) => string | number }[] = [
  { header: 'Assinante', tipo: 'txt', largura: 38, valor: (f) => f.cliente_nome || '' },
  { header: 'CPF/CNPJ', tipo: 'txt', largura: 20, valor: (f) => f.cliente_cpf_cnpj || '' },
  { header: 'UC', tipo: 'txt', largura: 16, valor: (f) => f.uc_codigo || '' },
  { header: 'Mês referência', tipo: 'txt', largura: 14, valor: (f) => f.mes_referencia || '' },
  { header: 'Usina (UG)', tipo: 'txt', largura: 24, valor: (f) => f.unidade_geradora_1 || '' },
  { header: 'Vencimento', tipo: 'data', largura: 12, valor: (f) => dataParaSerialExcel(dataVencimentoDe(f)) },
  { header: 'Pagamento', tipo: 'data', largura: 12, valor: (f) => dataParaSerialExcel(f.data_pgto_boleto) },
  { header: 'Dias após vencimento', tipo: 'num', largura: 12, valor: (f) => diasPagamentoAposVencimento(f) ?? '' },
  { header: 'Valor', tipo: 'brl', largura: 14, valor: (f) => valorDevido(f) },
  { header: 'Status', tipo: 'txt', largura: 14, valor: (f) => f.statusDerivado },
  { header: 'Boleto único', tipo: 'txt', largura: 10, valor: (f) => (Number(f.valor_emissao_boleto_unico) > 0 ? 'Sim' : 'Não') },
];

/**
 * @param parteVigor quando informada, entra a coluna "Parte da Vigor" logo depois
 *   de "Valor" (boleto único sem a Equatorial, parceira sem o percentual dela).
 *   Opcional: o Painel Financeiro não seleciona as colunas que a conta precisa.
 * @param nomes quando informada, a coluna "Assinante" passa a vir do cadastro da
 *   UC e entra "Titular da fatura" ao lado — são papéis diferentes, ver `nomesDaUc.ts`.
 */
export function planilhaBoletos(
  faturas: FaturaPainel[],
  nome = 'Boletos',
  parteVigor?: (f: FaturaPainel) => number,
  nomes?: (f: FaturaPainel) => { assinante: string; titular: string | null },
): Planilha {
  const comNomes = nomes
    ? [
        { header: 'Assinante', tipo: 'txt' as ColType, largura: 38, valor: (f: FaturaPainel) => nomes(f).assinante },
        { header: 'Titular da fatura', tipo: 'txt' as ColType, largura: 38, valor: (f: FaturaPainel) => nomes(f).titular || '' },
        ...COLUNAS_BOLETOS.slice(1),
      ]
    : COLUNAS_BOLETOS;
  const iValorBase = comNomes.findIndex((c) => c.header === 'Valor');
  const colunas = parteVigor
    ? [
        ...comNomes.slice(0, iValorBase + 1),
        { header: 'Parte da Vigor', tipo: 'brl' as ColType, largura: 14, valor: parteVigor },
        ...comNomes.slice(iValorBase + 1),
      ]
    : comNomes;
  const totals: (string | number)[] = colunas.map(() => '');
  totals[0] = `Total: ${faturas.length.toLocaleString('pt-BR')} boletos`;
  totals[iValorBase] = somaValorDevido(faturas);
  if (parteVigor) totals[iValorBase + 1] = faturas.reduce((s, f) => s + parteVigor(f), 0);
  return {
    nome,
    headers: colunas.map((c) => c.header),
    rows: faturas.map((f) => colunas.map((c) => c.valor(f))),
    colTypes: colunas.map((c) => c.tipo),
    widths: colunas.map((c) => c.largura),
    totals,
  };
}

// A aba recebe tudo que o card Emitido não conta. Deixou de ser só "sem
// cobrança e sem injeção" quando Cancelado e Quitado saíram do emitido
// (solicitações 5956 e 5957) — o nome antigo passaria a mentir sobre o
// conteúdo. Limite de 31 caracteres para nome de aba do Excel.
export const NOME_ABA_FORA_DO_FATURAMENTO = 'Fora do faturamento emitido';

/**
 * Abas de boletos do Excel completo. A aba "Boletos" é exatamente o card
 * Faturamento Emitido (mesma lista, mesmo total) — todos os outros cards são
 * recortes dela, então dá para refazer qualquer um filtrando a coluna Status.
 * O que o card deixa de fora ("Sem cobrança"/"Sem injeção") vai para uma aba
 * própria, em vez de inflar o total da aba principal: no teste com dados reais
 * (15/09/2026) a aba única somava R$ 5,94 mi contra R$ 5,89 mi no card.
 */
export function planilhasDeBoletos(
  filtrados: FaturaPainel[],
  nomes?: (f: FaturaPainel) => { assinante: string; titular: string | null },
): Planilha[] {
  const emitidos = DEFINICOES_KPI.emitido.filtrar(filtrados);
  const idsEmitidos = new Set(emitidos.map((f) => f.id));
  const fora = filtrados.filter((f) => !idsEmitidos.has(f.id));
  const abas = [planilhaBoletos(emitidos, 'Boletos', undefined, nomes)];
  if (fora.length > 0) abas.push(planilhaBoletos(fora, NOME_ABA_FORA_DO_FATURAMENTO, undefined, nomes));
  return abas;
}

const fmtBRL = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2 });
const fmtPct = (v: number) => `${(v * 100).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`;
const fmtDias = (v: number | null) => (v === null ? '—' : `${v.toLocaleString('pt-BR', { maximumFractionDigits: 1 })} dias`);

function linhasFiltros(filtros: FiltrosDescritos, geradoEm: Date): string[][] {
  return [
    ['Filtro · Período', filtros.periodo, ''],
    ['Filtro · Usina (UG)', filtros.unidadeGeradora, ''],
    ['Filtro · Status', filtros.status, ''],
    ['Filtro · Cliente', filtros.cliente || 'Todos', ''],
    ['Exportado em', geradoEm.toLocaleString('pt-BR'), ''],
  ];
}

export function planilhaResumo(kpis: Kpis, filtros: FiltrosDescritos, geradoEm: Date): Planilha {
  const rows: string[][] = [
    ...linhasFiltros(filtros, geradoEm),
    ['', '', ''],
    ['Faturamento Emitido', fmtBRL(kpis.emitido), `${kpis.qtdeBoletos.toLocaleString('pt-BR')} boletos`],
    ['Recebido', fmtBRL(kpis.recebido), `${fmtPct(kpis.recebidoPctEmitido)} do emitido`],
    ['A Vencer', fmtBRL(kpis.emAberto), `${fmtBRL(kpis.emAbertoValorVencendo7dias)} (${kpis.emAbertoQtdeVencendo7dias}) vencendo em 7 dias`],
    ['Vencido (Inadimplência)', fmtBRL(kpis.vencido), `${kpis.vencidoQtdeBoletos} boletos · ${kpis.vencidoClientesDistintos} clientes`],
    ['Taxa de Inadimplência', fmtPct(kpis.taxaInadimplencia), 'Vencido ÷ emitido'],
    ['Ticket Médio', fmtBRL(kpis.ticketMedio), ''],
    ['Prazo Médio de Recebimento', fmtDias(kpis.prazoMedioRecebimentoDias), `Da emissão ao pagamento · mediana ${fmtDias(kpis.prazoMedianoRecebimentoDias)}`],
    ['Pagamento vs. vencimento', fmtDias(kpis.desvioMedioVencimentoDias), `Negativo = paga adiantado · mediana ${fmtDias(kpis.desvioMedianoVencimentoDias)}${kpis.prazoAindaMaturando ? ' · período em curso' : ''}`],
    ['Recuperado', fmtBRL(kpis.recuperado), `${kpis.recuperadoQtdePagosComAtraso} boletos pagos com atraso`],
  ];
  return { nome: 'Resumo', headers: ['Indicador', 'Valor', 'Detalhe'], rows, colTypes: ['txt', 'txt', 'txt'], widths: [30, 24, 60] };
}

/** Aba de uma lista só (a do card aberto): filtros e descrição da lista no topo
 *  ficam numa aba "Sobre", para a aba de boletos continuar filtrável. */
export function planilhaSobreLista(titulo: string, descricao: string, filtros: FiltrosDescritos, geradoEm: Date): Planilha {
  const rows: string[][] = [
    ['Lista', titulo, ''],
    ['O que entra', descricao, ''],
    ...linhasFiltros(filtros, geradoEm),
  ];
  return { nome: 'Sobre', headers: ['Item', 'Valor', ''], rows, colTypes: ['txt', 'txt', 'txt'], widths: [22, 70, 4] };
}

export function planilhaEvolucaoMensal(pontos: PontoMensal[]): Planilha {
  return {
    nome: 'Evolução mensal',
    // "Mês", não "Mês referência": a série segue a base de data escolhida na
    // tela (referência, vencimento ou emissão) — ver `calcularPontosMensais`.
    headers: ['Mês', 'Emitido', 'Pago', 'Em aberto', 'Vencido', 'Taxa de inadimplência'],
    rows: pontos.map((p) => [p.mes, p.emitido, p.pago, p.emAberto, p.vencido, p.taxaInadimplencia * 100]),
    colTypes: ['txt', 'brl', 'brl', 'brl', 'brl', 'pct'],
    widths: [14, 16, 16, 16, 16, 12],
  };
}

export function planilhaAging(faixas: FaixaAging[]): Planilha {
  return {
    nome: 'Aging',
    headers: ['Faixa (dias de atraso)', 'Boletos', 'Valor'],
    rows: faixas.map((f) => [f.faixa, f.qtde, f.valor]),
    colTypes: ['txt', 'num', 'brl'],
    widths: [20, 10, 16],
    totals: ['Total', faixas.reduce((s, f) => s + f.qtde, 0), faixas.reduce((s, f) => s + f.valor, 0)],
  };
}

export function planilhaTopDevedores(devedores: DevedorTop[]): Planilha {
  return {
    nome: 'Top devedores',
    headers: ['#', 'Cliente', 'CPF/CNPJ', 'Boletos vencidos', 'Valor vencido'],
    rows: devedores.map((d, i) => [i + 1, d.cliente, d.cpfCnpj || '', d.qtdeBoletos, d.valorVencido]),
    colTypes: ['num', 'txt', 'txt', 'num', 'brl'],
    widths: [5, 38, 20, 12, 16],
  };
}

/** Pedaço seguro para nome de arquivo: sem acento, espaço vira hífen. */
export function slugArquivo(texto: string): string {
  return texto
    .normalize('NFD')
    .replace(/\p{Mn}/gu, '')
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase();
}
